import React, { useRef, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Container } from "reactstrap";
import dayjs from "dayjs";
import { usePropertyStore } from "../../contexts/PropertyStoreContext";
import useDeepLinkedPropertySwitch from "../../hooks/useDeepLinkedPropertySwitch";
import useRecommendationDeepLink from "../../hooks/useRecommendationDeepLink";
import { RECOMMENDATION_QUERY_PARAM } from "../../constants/recommendationDeepLink";
import { getIdTokenClaims } from "@hoteltradergit/ht-telemetry-ui-lib";
import BlackFridayModal from "../../components/black-friday/BlackFridayModal";
import BlackFridayStrip from "../../components/black-friday/BlackFridayStrip";
import { BLACK_FRIDAY_MODAL_IMAGES } from "../../components/black-friday/blackFridayAssets";
import { BLACK_FRIDAY_OPEN_STATE_KEY } from "../../constants/blackFriday";
import preloadImages from "../../utils/preloadImages";
import { pathWithoutParams } from "../../utils/urlParams";
import {
  findBlackFridayCard,
  hasSeenBlackFridayModal,
  markBlackFridayModalSeen,
} from "../../utils/blackFriday";
import {
  PROMO_EXPIRY_SOURCES,
  PROMO_QUERY_PARAM,
} from "../../constants/promoExpiry";
import { trackExpiringPromoCtaClicked } from "../../utils/promoExpiryAnalytics";
import PerformanceInsights from "../../components/performance/PerformanceInsights";
import HomepageBanner from "../../components/performance/homepage-banner/HomepageBanner";
import ActionCenter from "../../components/performance/action-center/ActionCenter";
import BookingPace from "../../components/performance/BookingPace";
import PromotionsSection from "../../components/performance/PromotionsSection";
import { usePromotionsData } from "../../hooks/usePromotionsData";
import { useActionCenterData } from "../../hooks/useActionCenterData";
import { useActionCenterV2Data } from "../../hooks/useActionCenterV2Data";
import useReloadOnPropertyChange from "../../hooks/useReloadOnPropertyChange";
import { Can, P, usePermission } from "../../rbac";
import "./performance.scss";

const Performance = () => {
  const { selectedProperty, isPilotHotel } = usePropertyStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // EO-1184 expiry email: /?propertyId=…&src=email lands here and opens Review
  // Your Promotions. The modal lives on the Action Center card, so the home page
  // is where the link belongs — it names a property's whole expiring set rather
  // than one promo, which is what ?promoId on /promotions is for.
  const deepLinkedPropertyId = searchParams.get(PROMO_QUERY_PARAM.propertyId);
  const deepLinkSource = searchParams.get(PROMO_QUERY_PARAM.source);
  const wantsExpiryReview =
    Boolean(deepLinkedPropertyId) &&
    PROMO_EXPIRY_SOURCES.includes(deepLinkSource);

  // Passing null when the link is not active means the hook needs no second
  // "is this live" condition of its own.
  const { isOnDeepLinkedProperty } = useDeepLinkedPropertySwitch(
    wantsExpiryReview ? deepLinkedPropertyId : null,
    {
      notFoundMessage:
        "Those promotions belong to a property you don't have access to",
    }
  );

  // Reported on arrival, which is the only place an email CTA can be counted —
  // the click happens in a mail client.
  const reportedCtaRef = useRef(null);
  useEffect(() => {
    if (!wantsExpiryReview || !isOnDeepLinkedProperty) return;
    if (reportedCtaRef.current === deepLinkedPropertyId) return;
    reportedCtaRef.current = deepLinkedPropertyId;
    trackExpiringPromoCtaClicked({ source: deepLinkSource });
  }, [
    wantsExpiryReview,
    isOnDeepLinkedProperty,
    deepLinkedPropertyId,
    deepLinkSource,
  ]);

  // Dropped once consumed so a refresh, or closing the modal, does not reopen it.
  const clearExpiryDeepLink = useCallback(() => {
    void navigate(
      pathWithoutParams(globalThis.location, [
        PROMO_QUERY_PARAM.propertyId,
        PROMO_QUERY_PARAM.source,
      ]),
      { replace: true }
    );
  }, [navigate]);
  // EO-1893 email link: /?recommendationId=… switches to the card's property and
  // pins the card first. The param must survive until the switch lands — a
  // property change reloads the page — and is dropped once the card is pinned.
  const linkedRecommendationId = searchParams.get(RECOMMENDATION_QUERY_PARAM);
  const { isReady: isOnRecommendationProperty } = useRecommendationDeepLink(
    linkedRecommendationId
  );
  const [pinnedRecommendationId, setPinnedRecommendationId] = useState(null);
  useEffect(() => {
    if (!linkedRecommendationId || !isOnRecommendationProperty) return;
    setPinnedRecommendationId(linkedRecommendationId);
    void navigate(
      pathWithoutParams(globalThis.location, [RECOMMENDATION_QUERY_PARAM]),
      { replace: true }
    );
  }, [linkedRecommendationId, isOnRecommendationProperty, navigate]);

  const { promotionsData, promotionsLoading } =
    usePromotionsData(selectedProperty);
  const {
    actionCenterData,
    actionCenterLoading,
    actionCenterSettled,
    refetch: refetchActionCenter,
  } = useActionCenterData(selectedProperty);
  const {
    actionCenterV2Data,
    actionCenterV2Loading,
    actionCenterV2Settled,
    refetch: refetchActionCenterV2,
  } = useActionCenterV2Data(selectedProperty);

  useReloadOnPropertyChange();

  const handlePromoUsed = () => {
    refetchActionCenter();
    refetchActionCenterV2();
  };

  // EO-1892: Black Friday strip (in the home banner's place) and pop-up. Both
  // CTAs open the card's Create Promo panel. The pop-up shows once per session;
  // the email link skips it and opens Create Promo directly.
  // Maybe Later hides the card for the session only (no refetch), so the strip
  // and pop-up step aside for it here, or their CTA would have nothing to open.
  const [dismissedRecommendationId, setDismissedRecommendationId] =
    useState(null);
  const liveBlackFridayCard = findBlackFridayCard(
    actionCenterV2Data,
    selectedProperty?.propertyId
  );
  const blackFridayCard =
    liveBlackFridayCard?.recommendationId === dismissedRecommendationId
      ? null
      : liveBlackFridayCard;
  // Only the Black Friday card's dismissal counts; another card's must not
  // overwrite it and bring the strip back.
  const dismissBlackFriday = (recommendationId) => {
    if (recommendationId === liveBlackFridayCard?.recommendationId) {
      setDismissedRecommendationId(recommendationId);
    }
  };
  // The strip's gate: without Create Promo the home banner stays instead.
  const { allowed: canCreatePromo } = usePermission(
    P.Home.ActionCenter.CreatePromo
  );
  const blackFridayId = blackFridayCard?.recommendationId ?? null;
  const showBlackFridayStrip = Boolean(blackFridayCard) && canCreatePromo;
  const userId = getIdTokenClaims()?.sub ?? "";
  const [isOfferModalOpen, setIsOfferModalOpen] = useState(false);
  const [openRecommendationId, setOpenRecommendationId] = useState(null);

  // A Black Friday panel asked for by the email link or another page, opened
  // once both feeds are ready.
  const [requestedOpenId, setRequestedOpenId] = useState(null);
  const linkOpenedRef = useRef(null);

  useEffect(() => {
    // Its CTA opens Create Promo, so it follows the same permission as the strip.
    if (!blackFridayId || !canCreatePromo) return;
    // The email link goes straight to Create Promo, without the pop-up.
    if (pinnedRecommendationId === blackFridayId) {
      if (linkOpenedRef.current === blackFridayId) return;
      linkOpenedRef.current = blackFridayId;
      markBlackFridayModalSeen(userId);
      setRequestedOpenId(blackFridayId);
      return;
    }
    // Wait while a link is still resolving: its card may be this one.
    if (linkedRecommendationId) return;
    if (hasSeenBlackFridayModal(userId)) return;
    // Open once the artwork is decoded, so it paints with the panel.
    let cancelled = false;
    // preloadImages never rejects; void marks the promise as handled.
    void preloadImages(BLACK_FRIDAY_MODAL_IMAGES).then(() => {
      if (!cancelled) setIsOfferModalOpen(true);
    });
    return () => {
      cancelled = true;
    };
  }, [
    blackFridayId,
    canCreatePromo,
    pinnedRecommendationId,
    linkedRecommendationId,
    userId,
  ]);

  const closeOfferModal = () => {
    markBlackFridayModalSeen(userId);
    setIsOfferModalOpen(false);
  };

  const reviewOffer = () => {
    closeOfferModal();
    setOpenRecommendationId(blackFridayId);
  };

  const currentDayName = dayjs().format("dddd");

  const actionCenterRef = useRef(null);
  const location = useLocation();

  const handleReviewAll = useCallback(() => {
    actionCenterRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  // Arriving via the Action Center popover CTA → auto-scroll to the section.
  // The scroll must wait for the action center data: the banner and cards
  // render only once it arrives, and they shift the section's position.
  const pendingScrollRef = useRef(false);

  useEffect(() => {
    if (location.state?.scrollToActionCenter) {
      pendingScrollRef.current = true;
      // Clear the flag so refresh/back doesn't re-scroll.
      window.history.replaceState({}, "");
    }
    // The Black Friday strip on another page sends the user here with its
    // card's Create Promo panel to open (EO-1892).
    const requested = location.state?.[BLACK_FRIDAY_OPEN_STATE_KEY];
    if (requested) {
      setRequestedOpenId(requested);
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  // Opened only once both feeds have settled: the second one landing re-renders
  // the card list, which closed a panel opened earlier.
  useEffect(() => {
    if (!requestedOpenId || requestedOpenId !== blackFridayId) return;
    // Same gate as Home's own strip: no Create Promo, no panel.
    if (!canCreatePromo) return;
    if (actionCenterLoading || actionCenterV2Loading) return;
    setOpenRecommendationId(requestedOpenId);
    setRequestedOpenId(null);
  }, [
    requestedOpenId,
    blackFridayId,
    canCreatePromo,
    actionCenterLoading,
    actionCenterV2Loading,
  ]);

  useEffect(() => {
    if (!pendingScrollRef.current) return;
    if (actionCenterLoading || !actionCenterData) return;
    pendingScrollRef.current = false;
    // Scroll after the banner/cards from this data have painted.
    requestAnimationFrame(() => handleReviewAll());
  }, [actionCenterData, actionCenterLoading, handleReviewAll]);

  return (
    <Container fluid className="performance-page">
      {/* The Black Friday strip tops the page (Figma 1:27626) and stands in for
          the opportunities banner while the offer is live. */}
      {showBlackFridayStrip && (
        <BlackFridayStrip
          card={blackFridayCard}
          onReview={() => setOpenRecommendationId(blackFridayId)}
        />
      )}
      <h1 className="performance-greeting">{`Happy ${currentDayName}!`}</h1>
      {!showBlackFridayStrip && (
        <Can permission={P.Home.View}>
          <HomepageBanner
            data={actionCenterData}
            v2Data={actionCenterV2Data}
            onReviewAll={handleReviewAll}
          />
        </Can>
      )}
      <PerformanceInsights />
      <Can permission={P.Home.View}>
        <div ref={actionCenterRef}>
          <ActionCenter
            data={actionCenterData}
            v2Data={actionCenterV2Data}
            loading={actionCenterLoading}
            v2Loading={actionCenterV2Loading}
            // Header count waits for both feeds, like the nav badge (EO-1834).
            feedsSettled={actionCenterSettled && actionCenterV2Settled}
            selectedProperty={selectedProperty}
            onPromoUsed={handlePromoUsed}
            autoOpenExpiryReview={wantsExpiryReview && isOnDeepLinkedProperty}
            onExpiryReviewOpened={clearExpiryDeepLink}
            pinnedRecommendationId={pinnedRecommendationId}
            openRecommendationId={openRecommendationId}
            onRecommendationOpened={() => setOpenRecommendationId(null)}
            onRecommendationDismissed={dismissBlackFriday}
          />
        </div>
        <BlackFridayModal
          open={isOfferModalOpen}
          card={blackFridayCard}
          onReview={reviewOffer}
          onClose={closeOfferModal}
        />
      </Can>
      <BookingPace />
      <PromotionsSection
        data={promotionsData}
        loading={promotionsLoading}
        isPilotHotel={isPilotHotel}
      />
    </Container>
  );
};

export default Performance;
